import { useTranslation } from 'react-i18next';
import { UtilityTypes } from '@d-orca/shared/trusstor';
import { getEquipmentIconFromType } from '@utils/equipmentIcons';
import { nameInitials } from '@utils/nameInitials';
import { professionChipStyle } from '@utils/professionColor';
import { tradeLabel } from '@utils/tradeLabel';
import type {
  EquipmentPresence,
  ManagerPresence,
  UtilityPresence,
} from '@store/selectors/monitor.selectors';
import type { FloorTradeRow } from './floorTrades';
import styles from './mainView.module.css';

interface IFloorPopoverContentProps {
  title: string;
  managers: ManagerPresence[];
  trades: FloorTradeRow[];
  equipment: EquipmentPresence[];
  utilities: UtilityPresence[];
  isAdmin: boolean;
}

/**
 * Body of the floor-details popover. Rendered inside Base UI's
 * `Tooltip.Popup` — Tooltip handles position, portal, and dismissal.
 * Content is the same as Trusstor's floor-details panel: floor title,
 * one chip per trade, and (admin-only) per-tag detail rows.
 */
export function FloorPopoverContent({
  title,
  managers,
  trades,
  equipment,
  utilities,
  isAdmin,
}: IFloorPopoverContentProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.floorPopoverTitle}>{title}</div>
      {managers.length > 0 && (
        <ul className={styles.floorPopoverManagerList}>
          {managers.map((mgr) => (
            <li key={mgr.id} className={styles.floorPopoverManager}>
              <span
                className={styles.floorPopoverManagerAvatar}
                style={professionChipStyle(mgr.backgroundColor)}
              >
                {nameInitials(mgr.name)}
              </span>
              <span className={styles.floorPopoverManagerName}>{mgr.name}</span>
            </li>
          ))}
        </ul>
      )}
      <ul className={styles.floorPopoverList}>
        {trades.map((trade) => (
          <li key={trade.professionId} className={styles.floorPopoverTrade}>
            <span className={styles.floorPopoverChip} style={professionChipStyle(trade.color)}>
              {tradeLabel(t, trade.tradeId)}
            </span>
            {isAdmin && trade.tags.length > 0 && (
              <ul className={styles.floorPopoverTagList}>
                {trade.tags.map((tag) => (
                  <li key={tag.tagId} className={styles.floorPopoverTag}>
                    {tag.name && (
                      <span className={styles.floorPopoverTagField}>
                        <span className={styles.floorPopoverTagLabel}>
                          {t('monitor.floorPopover.name')}:
                        </span>
                        <span>{tag.name}</span>
                      </span>
                    )}
                    <span className={styles.floorPopoverTagField}>
                      <span className={styles.floorPopoverTagLabel}>{t('monitor.floorPopover.tagId')}:</span>
                      <span className={styles.floorPopoverTagId}>{tag.tagId}</span>
                    </span>
                    {tag.professionLabel && (
                      <span className={styles.floorPopoverTagField}>
                        <span className={styles.floorPopoverTagLabel}>
                          {t('monitor.floorPopover.profession')}:
                        </span>
                        <span>{tag.professionLabel}</span>
                      </span>
                    )}
                    {tag.batteryLevel !== undefined && (
                      <span className={styles.floorPopoverTagField}>
                        <span className={styles.floorPopoverTagLabel}>
                          {t('monitor.floorPopover.battery')}:
                        </span>
                        <span>{tag.batteryLevel}%</span>
                      </span>
                    )}
                    {tag.timestamp && (
                      <span className={styles.floorPopoverTagTimestamp}>{tag.timestamp}</span>
                    )}
                    {tag.status && (
                      <span className={styles.floorPopoverTagField}>
                        <span className={styles.floorPopoverTagLabel}>
                          {t('monitor.floorPopover.status')}:
                        </span>
                        <span>{tag.status}</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
        {equipment.length > 0 && (
          <li className={styles.floorPopoverTrade}>
            <ul className={styles.floorPopoverEquipList}>
              {equipment.map((eq) => (
                <li key={eq.type} className={styles.floorPopoverEquipChip}>
                  <img
                    className={styles.floorPopoverEquipIcon}
                    src={getEquipmentIconFromType(eq.type)}
                    alt=""
                  />
                  <span>{eq.type}</span>
                  <span className={styles.floorPopoverEquipCount}>{eq.count}</span>
                </li>
              ))}
            </ul>
          </li>
        )}
        {utilities.length > 0 && (
          <li className={styles.floorPopoverTrade}>
            <ul className={styles.floorPopoverUtilityList}>
              {utilities.map((u) => (
                <li key={u.id} className={styles.floorPopoverUtility}>
                  <img
                    className={styles.floorPopoverEquipIcon}
                    src={getEquipmentIconFromType(u.type)}
                    alt=""
                  />
                  <span className={styles.floorPopoverUtilityName}>{u.name}</span>
                  {u.type === UtilityTypes.scissorsLift && u.isLive && (
                    <span className={styles.liveBadge} title={t('monitor.floorPopover.live')}>
                      <span className={styles.liveDot} aria-hidden="true" />
                      {t('monitor.floorPopover.live')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </li>
        )}
      </ul>
    </>
  );
}
